import React from 'react';
import classNames from "classnames";
import {SnackbarContent, closeSnackbar, SnackbarKey} from "notistack";


import type {NotificationParams, OurVariantType} from "../../hooks/useNotification";


import {CloseIcon} from "../icons/CloseIcon";
import {CheckIcon} from "../icons/CheckIcon";
import {DangerIcon} from "../icons/DangerIcon";
import {WarningIcon} from "../icons/WarningIcon";
import {InfoIcon} from "../icons/InfoIcon";
import {TextButton} from "../TextButton";


import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";


import './SnackbarBlock.css';


export interface SnackbarProps extends NotificationParams {
    id: SnackbarKey;
}

export const SnackbarBlock = React.forwardRef<HTMLDivElement, SnackbarProps>((
    {
        message,
        title,
        variant,
        style,
        id,
        action,
        actionDef,
        Icon,
        isCloseIcon = true,
    },
    ref
) => {
    const isMobile = screenSizeProvider.view !== 'desktop';
    const IconActual = Icon ? Icon : getVariantIcon(variant);
    if (!title) {
        title = message;
        message = null;
    }
    return (
        <SnackbarContent
            className={classNames({
                'SnackbarBlock': true,
                'SnackbarBlock--noIcon': !IconActual,
                'SnackbarBlock--success': variant === 'success',
                'SnackbarBlock--info': variant === 'info',
                'SnackbarBlock--warning': variant === 'warning',
                'SnackbarBlock--danger': variant === 'error',
                'SnackbarBlock--primary': variant === 'primary',
                'SnackbarBlock--ai': variant === 'ai',
            })}
            ref={ref}
            style={style}
            id={id as string}
        >
            <div className={classNames({
                'SnackbarBlock-header-line': true,
                'SnackbarBlock-header-withButton': action && isMobile
            })}>
                <div className={'SnackbarBlock-header title-medium'}>
                    {IconActual && <IconActual className={'SnackbarBlock-icon'} size={20}/>}
                    {title}
                </div>
                <div className={classNames({
                    "SnackbarBlock-action": true,
                    "SnackbarBlock-action-withButton": action && isMobile
                })}>
                    {
                        action
                            ? (
                                typeof action === 'function'
                                    ? <span className="SnackbarBlock__btn">{action(id)}</span>
                                    : <span className="SnackbarBlock__btn">{action}</span>
                            )
                            : null
                    }
                    {
                        actionDef
                            ? (
                                <TextButton
                                    text={actionDef.text}
                                    onClick={() => {
                                        actionDef.onClick();
                                        closeSnackbar(id)
                                    }}
                                    className={'SnackbarBlock__text-btn title-medium'}
                                />
                            )
                            : null
                    }
                    {
                        isCloseIcon &&
                        <CloseIcon className={'SnackbarBlock-icon-close'}
                                   additionalPadding={2}
                                   size={20}
                                   onClick={() => closeSnackbar(id)}
                        />
                    }
                </div>
            </div>
            {message ? <div className={'SnackbarBlock-message body-large'}>{message}</div> : null}
        </SnackbarContent>
    )
});

export const getVariantIcon = (variant?: OurVariantType) => {
    switch (variant) {
        case "info":
            return InfoIcon;
        case "success":
            return CheckIcon;
        case "warning":
            return WarningIcon;
        case "error":
            return DangerIcon;
        case "default":
        default:
            return null;
    }
}